// SigmaStudioFMEA.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import './SigmaStudio.css'; // assuming same CSS file

const SigmaStudioFMEA = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [remarks, setRemarks] = useState("");
  const [processSteps, setProcessSteps] = useState("");       // one per line
  const [fmeaRowsText, setFmeaRowsText] = useState("");       // tab/comma separated or free format
  const [showHelp, setShowHelp] = useState(false);
  const [fmeaResult, setFmeaResult] = useState(null);
  const [error, setError] = useState("");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const populateExample = () => {
    setError("");
    setFmeaResult(null);

    setTitle("CNC Turning Process – Shaft Component");
    setRemarks("Process FMEA for critical machining operation – reviewed by QE, ME, Operator");
    setProcessSteps("1. Material Loading\n2. Facing & Centering\n3. Rough Turning OD\n4. Finish Turning OD\n5. Grooving\n6. Part Unloading");

    setFmeaRowsText(
      "1. Material Loading | Material not properly clamped | Part slippage during machining | 9 | Wrong loading procedure | 4 | Operator training + visual aid | 3 | 108 | Add poka-yoke clamp sensor\n" +
      "1. Material Loading | Wrong material loaded | Incorrect hardness / grade | 8 | Material mix-up | 3 | Incoming inspection + color coding | 2 | 48\n" +
      "3. Rough Turning OD | Tool wear not detected | Oversize / poor surface | 7 | No tool life monitoring | 6 | Operator checks every 50 pcs | 4 | 168 | Implement auto tool wear compensation\n" +
      "4. Finish Turning OD | Vibration during cut | Poor surface finish | 6 | Incorrect cutting parameters | 5 | Standard parameters sheet | 3 | 90"
    );
  };

  const parseProcessSteps = (str) => str.split('\n').map(l => l.trim()).filter(Boolean);

  const parseFmeaRows = (text) => {
    const lines = text.trim().split('\n').filter(Boolean);
    const rows = [];

    for (let line of lines) {
      // Flexible split – tab, multiple spaces, or single comma
      const parts = line.split(/\t+| {2,}| {1,}\| {1,}/).map(p => p.trim());

      if (parts.length < 9) continue; // minimum needed

      const [
        stepRef, failureMode, failureEffect, severity,
        failureCause, occurrence, currentControl, detection, rpn, // rpn can be ignored – we recalculate
        recommendedAction = ""
      ] = parts;

      const sev = parseInt(severity, 10);
      const occ = parseInt(occurrence, 10);
      const det = parseInt(detection, 10);

      if (isNaN(sev) || isNaN(occ) || isNaN(det)) continue;

      const calculatedRPN = sev * occ * det;

      rows.push({
        step: stepRef,
        mode: failureMode,
        effect: failureEffect,
        sev: sev,
        cause: failureCause,
        occ: occ,
        control: currentControl,
        det: det,
        rpn: calculatedRPN,
        action: recommendedAction,
      });
    }
    return rows;
  };

  const calculateFMEA = () => {
    setError("");
    setFmeaResult(null);

    if (!title.trim()) return setError("Title is required.");

    const steps = parseProcessSteps(processSteps);
    if (steps.length < 1) return setError("Enter at least one process step.");

    const rows = parseFmeaRows(fmeaRowsText);
    if (rows.length < 1) {
      return setError("No valid FMEA rows parsed. Use format: Step | Failure Mode | Effect | Sev | Cause | Occ | Control | Det | RPN (optional) | Action");
    }

    // Sort by RPN descending for prioritization
    rows.sort((a, b) => b.rpn - a.rpn);

    const highRisk = rows.filter(r => r.rpn >= 100);
    const mediumRisk = rows.filter(r => r.rpn >= 40 && r.rpn < 100);
    const lowRisk = rows.filter(r => r.rpn < 40);

    setFmeaResult({
      title,
      remarks,
      totalItems: rows.length,
      highRiskCount: highRisk.length,
      rows: rows,
      summary: {
        highestRPN: rows[0]?.rpn || 0,
        priorityActions: highRisk.length > 0 
          ? "Immediate action required on high RPN items (≥100)"
          : mediumRisk.length > 0 
            ? "Focus on medium-high RPN items"
            : "All risks appear controlled",
      }
    });
  };

  const renderHelp = () => (
    <div className="mt-6 p-6 bg-gray-900 rounded-xl border border-teal-900/50">
      <h2 className="text-xl lg:text-2xl font-bold text-yellow-300 mb-6 text-center">
        Help – Process FMEA (Failure Mode & Effects Analysis)
      </h2>
      <p className="mb-4">
        FMEA helps identify what can go wrong in a process, how bad it would be, how likely it is, and whether we can detect it — before it happens.
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>List process steps in order (one per line)</li>
        <li>For each potential failure, enter: mode, effect, severity (1–10), cause, occurrence (1–10), current controls, detection (1–10)</li>
        <li>RPN = Severity × Occurrence × Detection (higher = more urgent)</li>
        <li>Typical action thresholds: RPN ≥ 100 → act now, ≥ 40–80 → review</li>
        <li>Use real process knowledge from operators, engineers, quality team</li>
      </ul>
      <p className="mb-4 font-medium text-teal-300">Severity / Occurrence / Detection scales:</p>
      <ul className="list-decimal pl-6 space-y-1 text-sm">
        <li>9–10 = Very high (safety, regulation, major loss)</li>
        <li>5–8  = Moderate to high</li>
        <li>1–4  = Low to very low</li>
      </ul>
      <div className="text-center mt-8">
        <button onClick={() => setShowHelp(false)} className="w-40 px-2 py-2 bg-teal-700 hover:bg-teal-600 rounded-lg font-medium">
          Close Help
        </button>
      </div>
    </div>
  );

  const renderResults = (result) => {
    if (!result) return null;

    return (
      <div className="mt-8 p-6 bg-gray-950 border border-gray-700 rounded-xl">
        <h2 className="text-2xl text-teal-300 mb-4 text-center">{result.title}</h2>
        {result.remarks && <p className="text-gray-300 mb-6 text-center italic">{result.remarks}</p>}

        <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
          <h3 className="text-xl text-teal-300 mb-3">FMEA Summary</h3>
          <p>Total failure modes identified: <strong>{result.totalItems}</strong></p>
          <p>High priority (RPN ≥ 100): <strong className="text-red-400">{result.highRiskCount}</strong></p>
          <p className="font-medium mt-2">{result.summary.priorityActions}</p>
          <p>Highest RPN: <strong>{result.summary.highestRPN}</strong></p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-200 border-collapse">
            <thead>
              <tr className="bg-gray-800">
                <th className="p-3 border-b border-gray-700">Step</th>
                <th className="p-3 border-b border-gray-700">Failure Mode</th>
                <th className="p-3 border-b border-gray-700">Effect</th>
                <th className="p-3 border-b border-gray-700">Sev</th>
                <th className="p-3 border-b border-gray-700">Cause</th>
                <th className="p-3 border-b border-gray-700">Occ</th>
                <th className="p-3 border-b border-gray-700">Current Control</th>
                <th className="p-3 border-b border-gray-700">Det</th>
                <th className="p-3 border-b border-gray-700 font-bold">RPN</th>
                <th className="p-3 border-b border-gray-700">Recommended Action</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, idx) => (
                <tr key={idx} className={row.rpn >= 100 ? "bg-red-950/40" : row.rpn >= 40 ? "bg-yellow-950/30" : ""}>
                  <td className="p-3 border-b border-gray-700">{row.step}</td>
                  <td className="p-3 border-b border-gray-700">{row.mode}</td>
                  <td className="p-3 border-b border-gray-700">{row.effect}</td>
                  <td className="p-3 border-b border-gray-700 text-center">{row.sev}</td>
                  <td className="p-3 border-b border-gray-700">{row.cause}</td>
                  <td className="p-3 border-b border-gray-700 text-center">{row.occ}</td>
                  <td className="p-3 border-b border-gray-700">{row.control}</td>
                  <td className="p-3 border-b border-gray-700 text-center">{row.det}</td>
                  <td className="p-3 border-b border-gray-700 font-bold text-center">{row.rpn}</td>
                  <td className="p-3 border-b border-gray-700">{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-black [&_p]:text-white [&_li]:text-gray-100 text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            📏 Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">FMEA – Failure Mode & Effects Analysis for MSMEs</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-sm">
                ⏻ Logout
              </button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="px-3 py-1 rounded border border-gray-600 text-sm">
              🔐 Login
            </button>
          )}
        </div>

        <button className="md:hidden text-2xl text-teal-300" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-700 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-6 py-2 bg-red-700 rounded">Logout</button>
            </div>
          ) : (
            <div className="text-center">
              <button onClick={() => navigate("/login")} className="px-6 py-2 border rounded">Login</button>
            </div>
          )}
        </div>
      )}

      <main className="flex-1 p-4 pb-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-3 gap-2 place-items-center mb-4">
          <button onClick={populateExample} className="text-sm px-3 py-2 bg-green-700 hover:bg-green-600 rounded-md font-medium">
            Populate Example
          </button>
          <button onClick={() => setShowHelp(!showHelp)} className="text-sm px-3 py-2 bg-blue-700 hover:bg-blue-600 rounded-md font-medium">
            {showHelp ? "Hide" : "Show"} Help
          </button>
        </div>

        {showHelp && renderHelp()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Study Title</label>
            <input
              type="text"
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. CNC Turning Process – Shaft Component"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-cyan-300 font-medium">Remarks / Notes</label>
          <textarea
            className="w-full h-24 p-3 bg-gray-900 border border-gray-700 rounded text-white"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Team members, date, process name, special conditions..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Process Steps / Operations (one per line)</label>
            <textarea
              className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
              value={processSteps}
              onChange={(e) => setProcessSteps(e.target.value)}
              placeholder="1. Material Loading\n2. Facing & Centering\n3. Rough Turning..."
            />
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">
              FMEA Rows (one per failure mode – use | or tab to separate)
            </label>
            <textarea
              className="w-full h-64 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
              value={fmeaRowsText}
              onChange={(e) => setFmeaRowsText(e.target.value)}
              placeholder={
                "Step | Failure Mode | Effect | Sev | Cause | Occ | Current Control | Det | (RPN optional) | Recommended Action\n" +
                "1. Loading | Part slippage | Damage / scrap | 8 | Poor clamping | 5 | Training | 4 | Poka-yoke clamp"
              }
            />
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculateFMEA}
            className="w-48 py-2 bg-purple-700 hover:bg-purple-600 rounded-lg font-medium disabled:opacity-50"
            disabled={!title.trim() || !processSteps.trim() || !fmeaRowsText.trim()}
          >
            Calculate FMEA
          </button>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-red-900/70 border border-red-700 rounded text-red-100 text-center">
            {error}
          </div>
        )}

        {fmeaResult && renderResults(fmeaResult)}
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions
          </div>
          <div className="flex flex-wrap gap-6">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioFMEA;